<template>
  <v-sheet>
    <Confirm
      :confirm.sync="confirmDelete"
      :callback="remove"
      title="Confirm Delete"
      :message="`Are you sure you want to permanently delete the selected file: ${selectedFile && selectedFile.file_name}?`"
    ></Confirm>
    <slot></slot>
    <v-card outlined :loading="loading">
      <v-card-title v-if="icon && title">
        <v-icon :color="iconColor" large left v-if="icon">{{icon}}</v-icon>
        <span class="title font-weight-light" v-if="title">{{title}}</span>
      </v-card-title>
      <v-card-text>
        <p v-if="description">{{description}}</p>
        <v-file-input
          v-model="uploadFiles"
          :accept="fileTypes"
          :multiple="multiple"
          @change="upload()"
          :label="label"
          class="file-input"
          v-if="!loadedFiles || !loadedFiles.length"
        ></v-file-input>
        <v-select
          v-model="selectedFile"
          :items="loadedFiles"
          item-text="file_name"
          item-value="id"
          :label="label"
          :return-object="true"
          :clearable="clearable"
          v-if="loadedFiles && loadedFiles.length && loadedFiles.length > 1"
        ></v-select>
        <v-text-field
          v-model="selectedFile.file_name"
          :label="label"
          v-if="loadedFiles && loadedFiles.length && loadedFiles.length === 1"
          readonly
        ></v-text-field>
      </v-card-text>
      <v-card-actions v-if="selectedFile">
        <v-spacer></v-spacer>
        <v-btn color="accent" text @click.stop="replace()" v-if="actions.includes('replace')">Replace</v-btn>
        <v-btn color="accent" text @click.stop="clear()" v-if="actions.includes('clear')">Clear</v-btn>
        <v-btn
          color="red darken-1"
          icon
          @click.stop="confirmDelete = true"
          v-if="actions.includes('remove')"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
        <v-btn
          color="accent"
          icon
          @click.stop="download(selectedFile)"
          title="Download"
          v-if="actions.includes('download')"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-sheet>
</template>

<script>
import axios from "axios";
import { mapState, mapGetters } from "vuex";
import { models } from "feathers-vuex";
import Confirm from "./Confirm";

export default {
  props: {
    value: {
      type: Object,
    },
    label: {
      type: String,
      default: "File",
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
    },
    icon: {
      type: String,
      default: "mdi-file",
    },
    iconColor: {
      type: String,
    },
    fileTypes: {
      type: String,
      default: ".csv",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: () => {
        return ["replace", "download", "remove"];
      },
    },
    id: {
      type: [String, Number, Object],
    },
    data: {
      type: Object,
      default: () => {
        return {};
      },
    },
    uploaded: {
      type: Function,
      default: () => {
        return;
      },
    },
    removed: {
      type: Function,
      default: () => {
        return;
      },
    },
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    Confirm,
  },
  data() {
    return {
      loading: false,
      fileId: null,
      loadedFiles: [],
      uploadFiles: null,
      selectedFile: null,
      confirmDelete: false,
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    ...mapState("apiAuth", ["accessToken"]),
    ...mapGetters("config", ["botApiUrl"]),
    filesUrl() {
      return new URL(
        process.env.VUE_APP_FILES_PATH,
        this.botApiUrl || process.env.VUE_APP_API_URL
      ).toString();
    },
  },
  methods: {
    replace() {
      this.data.replace = this.loadedFiles && this.loadedFiles.length && this.loadedFiles[0];
      this.uploadFiles = null;
      this.loadedFiles = null;
      this.selectedFile = null;
    },
    clear() {
      this.uploadFiles = null;
      this.loadedFiles = null;
      this.selectedFile = null;
    },
    async remove() {
      await this.selectedFile.remove();
      this.removed(this.selectedFile, this.loadedFiles);
      this.uploadFiles = null;
      this.loadedFiles = null;
      this.selectedFile = null;
    },
    selectFile(file) {
      if (this.selectedFile && file.id === this.selectedFile.id) {
        this.selectedFile = null;
      } else {
        this.selectedFile = file;
      }
    },
    uploadCallback(files) {
      if (this.multiple) {
        this.selectedFile = files;
      } else {
        this.loadedFiles = [];
        this.selectedFile = files[0];
      }
      this.uploaded(files);
      this.$emit("input", files);
      this.loadedFiles = this.loadedFiles.concat(files);
      this.loading = false;
    },
    async load() {
      const { File } = models.bot;
      let uploaded = null;

      if (this.id) {
        uploaded = await File.get(this.id);
        this.selectedFile = uploaded;
      } else if (this.multiple) {
        uploaded = await File.find({ query: this.data });
      }
      if (!uploaded) return;
      this.loadedFiles = Array.isArray(uploaded) ? uploaded : [uploaded];
    },
    async download(file) {
      let getUrl = new URL(
        file.id,
        `${this.filesUrl}/`
      );

      let fileData = await axios.get(getUrl.toString(), {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([fileData.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", file.file_name); //any other extension
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    async upload() {
      if (!this.uploadFiles) return;
      this.loading = true;

      let formData = new FormData();

      this.uploadFiles.forEach((file) => {
        formData.append("file[]", file, file.name);
      });

      for (const key in this.data) {
        if (Object.prototype.hasOwnProperty.call(this.data, key)) {
          const value = this.data[key];
          formData.append(key, value);
        }
      }

      try {
        let upload = await axios.post(this.filesUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
        if (upload && upload.data) {
          this.uploadCallback(upload.data);
        }
      } catch (error) {
        console.log(`File upload failed:`, error);
        this.loading = false;
      }
    },
  },
  watch: {
    id(now, before) {
      if (now && now !== before) {
        this.load();
      }
    },
  },
};
</script>

<style lang="scss">
.file-input {
  font-size: 1rem;
}

.file-display {
  font-size: 1rem;
}
</style>